import React from 'react';
import { Box, Skeleton } from '@mui/material';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';
import { InfoOutlined, VisibilityOff } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { processContent } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/utils';
import { styled } from '@mui/material/styles';
import {
  useKeywordsDisplayStore,
  useContentViewStore
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import {
  ContentCardContainer,
  ContentCardHeader
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/UtilizationCardContainer';
import { KeywordContent } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';

type CardProps = {
  title: string;
  content: KeywordContent[];
  flexHeight?: string;
  boxHeight?: string;
  className?: string;
  placeholderText?: string;
};

export const ContentCard = ({ title, content, placeholderText = 'No content available' }: CardProps) => {
  const { showKeywords } = useKeywordsDisplayStore();

  return (
    <ContentCardContainer
      sx={{
        padding: '0px',
        width: '50%'
      }}
    >
      <ContentCardHeader title={title} />
      <Box
        sx={{
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          padding: '8px',
          borderRadius: '8px',
          height: 'fit-content',
          overflow: 'auto',
          gap: '4px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}
      >
        {content && content.length > 0 ? (
          processContent({ phrases: content }, showKeywords)
        ) : (
          <Text sx={{ color: colors.errorRed }}>{placeholderText}</Text>
        )}
      </Box>
    </ContentCardContainer>
  );
};

export const ContentComparisonHeader = () => {
  const { showKeywords, setShowKeywords } = useKeywordsDisplayStore();
  const { currentView, toggleView } = useContentViewStore();

  const handleToggleKeywords = () => {
    setShowKeywords(!showKeywords);
  };

  const handleToggleView = () => {
    toggleView();
  };

  return (
    <Box sx={{ mb: 3, mt: 2 }}>
      <Flex alignItems="center" mb={2} gap={16}>
        {currentView === 'profile' ? (
          // Profile view - only show toggle button
          <Flex justifyContent="flex-end" alignItems="center" flexGrow={1}>
            <SlButton variant="normal" onClick={handleToggleView}>
              <Text variant="body2" sx={{ color: colors.darkBlue, fontWeight: 500, fontSize: '14px' }}>
                View Content
              </Text>
            </SlButton>
          </Flex>
        ) : (
          // Content view - show all elements
          <>
            <Flex flexGrow={1}>
              <Text variant="h3" fontWeight={700} sx={{ color: colors.darkBlue }}>
                Current Content
              </Text>
            </Flex>

            <Flex justifyContent="space-between" alignItems="center" width="50%">
              <Flex alignItems="center">
                <Text variant="h3" fontWeight={700} sx={{ color: colors.darkBlue, mr: 1 }}>
                  New Content
                </Text>
                <Tooltip title="AI-generated content based on profile and product information">
                  <IconButton size="small">
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Flex>

              <Flex gap={12}>
                <SlButton
                  startIcon={
                    <VisibilityOff
                      sx={{
                        color: colors.darkBlue,
                        marginLeft: '-13px',
                        marginRight: '8px',
                        width: '16px',
                        height: '16px'
                      }}
                    />
                  }
                  variant="normal"
                  onClick={handleToggleKeywords}
                >
                  <Text variant="body2" sx={{ color: colors.darkBlue, fontWeight: 500, fontSize: '14px' }}>
                    {showKeywords ? 'Hide keywords' : 'Show keywords'}
                  </Text>
                </SlButton>
                <SlButton variant="normal" onClick={handleToggleView}>
                  <Text variant="body2" sx={{ color: colors.darkBlue, fontWeight: 500, fontSize: '14px' }}>
                    {currentView === 'content' ? 'View Profile' : 'View Content'}
                  </Text>
                </SlButton>
              </Flex>
            </Flex>
          </>
        )}
      </Flex>
    </Box>
  );
};

export const ContentCardSkeleton = ({
  title,
  boxHeight = '144px'
}: Omit<CardProps, 'content' | 'blueTags' | 'greenTags' | 'placeholderText'>) => {
  return (
    <ContentCardContainer
      sx={{
        width: '100%'
      }}
    >
      <ContentCardHeader title={title} />
      <Box
        sx={{
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: 400,
          padding: '8px',
          backgroundColor: colors.graysPrimaryOpaque,
          borderRadius: '8px',
          width: '100%',
          height: boxHeight,
          overflow: 'auto'
        }}
      >
        <Skeleton variant="text" width="90%" height={24} />
        <Skeleton variant="text" width="80%" height={24} />
        <Skeleton variant="text" width="85%" height={24} />
        <Skeleton variant="text" width="70%" height={24} />
      </Box>
    </ContentCardContainer>
  );
};

export const NoRoundedBottomContentCard = styled(ContentCard)({
  borderRadius: '8px 8px 0 0'
});

export const NoRoundedBottomContentCardSkeleton = styled(ContentCardSkeleton)({
  borderRadius: '8px 8px 0 0'
});

export const ContentComparisonHeaderV2 = () => {
  const { showKeywords, setShowKeywords } = useKeywordsDisplayStore();

  const handleToggleKeywords = () => {
    setShowKeywords(!showKeywords);
  };

  return (
    <Box sx={{ mb: 3, mt: 2 }}>
      <Flex alignItems="center" mb={2} gap={16}>
        <Flex flexGrow={1}>
          <Text variant="h3" fontWeight={700} sx={{ color: colors.darkBlue }}>
            Current Content
          </Text>
        </Flex>

        <Flex justifyContent="space-between" alignItems="center" width="50%">
          <Flex alignItems="center">
            <Text variant="h3" fontWeight={700} sx={{ color: colors.darkBlue, mr: 1 }}>
              New Content
            </Text>
            <Tooltip title="AI-generated content based on profile and product information">
              <IconButton size="small">
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Flex>

          <Flex gap={12}>
            <SlButton
              startIcon={
                <VisibilityOff
                  sx={{
                    color: colors.darkBlue,
                    marginLeft: '-13px',
                    marginRight: '8px',
                    width: '16px',
                    height: '16px'
                  }}
                />
              }
              variant="normal"
              onClick={handleToggleKeywords}
            >
              <Text variant="body2" sx={{ color: colors.darkBlue, fontWeight: 500, fontSize: '14px' }}>
                {showKeywords ? 'Hide keywords' : 'Show keywords'}
              </Text>
            </SlButton>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

// Export new library components
export { LibraryReviewHeader } from './LibraryReviewHeader';
export { VersionComparisonCard } from './VersionComparisonCard';
